import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ruler, Trash, PaperPlaneTilt } from "@phosphor-icons/react";
import { portalApi } from "@/lib/portalApi";

// Shared list of a customer's saved cut pieces. Pass projectId to scope it to one
// project; omit it to list every saved cut (personal list). onCount reports the count
// so the parent can hide its heading when empty.
export const SavedCutList = ({ projectId = null, emptyHint = true, onCount }) => {
  const [cuts, setCuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(() => {
    portalApi
      .get("/saved-cuts")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        const next = projectId ? list.filter((c) => c.project_id === projectId) : list;
        setCuts(next);
        onCount?.(next.length);
      })
      .catch(() => setCuts([]))
      .finally(() => setLoading(false));
  }, [projectId, onCount]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (c) => {
    try {
      await portalApi.delete(`/saved-cuts/${c.id}`);
      setCuts((x) => {
        const next = x.filter((y) => y.id !== c.id);
        onCount?.(next.length);
        return next;
      });
      window.dispatchEvent(new Event("saved-cuts-changed"));
      toast.success("Cut removed");
    } catch {
      toast.error("Could not remove that cut");
    }
  };

  const quote = (c) => {
    const params = new URLSearchParams({
      slab: c.slab_id || "",
      color: c.color || "",
      item: c.item || c.code || "",
      size: `${c.length}" x ${c.width}"`,
      length: String(c.length || ""),
      width: String(c.width || ""),
      area: String(c.area || ""),
      photo: c.photo || "",
    });
    navigate(`/portal/request?${params.toString()}`);
  };

  if (loading) return null;
  if (!cuts.length)
    return emptyHint ? (
      <p className="text-sm text-slate-400" data-testid="portal-saved-cuts-empty">
        No saved cuts yet. Size a piece on any slab and tap “Save my cut”.
      </p>
    ) : null;

  return (
    <div className="space-y-2" data-testid="portal-saved-cuts-list">
      {cuts.map((c) => (
        <div
          key={c.id}
          data-testid={`portal-saved-cut-${c.id}`}
          className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 p-3.5"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#c39e6d]/15 text-[#a9855a]">
            <Ruler size={16} weight="bold" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-slate-900">
              {c.color || "Slab"}
              {c.item ? ` · item ${c.item}` : c.code ? ` · ${c.code}` : ""}
            </span>
            <span className="block text-xs text-slate-500">
              {c.length}&quot; × {c.width}&quot;
              {c.area ? ` · ${c.area} ft²` : ""}
              {!projectId && c.project_name ? ` · ${c.project_name}` : ""}
            </span>
            {c.note && (
              <span className="mt-0.5 block truncate text-xs text-slate-400">{c.note}</span>
            )}
          </span>
          <button
            type="button"
            data-testid={`portal-saved-cut-quote-${c.id}`}
            onClick={() => quote(c)}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#c39e6d] px-3.5 py-2 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-[#a9855a]"
          >
            <PaperPlaneTilt size={13} weight="bold" /> Quote it
          </button>
          <button
            type="button"
            data-testid={`portal-saved-cut-remove-${c.id}`}
            onClick={() => remove(c)}
            aria-label="Remove saved cut"
            className="rounded-full p-2 text-slate-400 transition-colors duration-150 hover:bg-red-50 hover:text-red-600"
          >
            <Trash size={15} weight="bold" />
          </button>
        </div>
      ))}
    </div>
  );
};

export default SavedCutList;
